<script setup lang="ts">
import { computed, watch, ref } from 'vue';
import { useCelebrationStore } from '@/stores/celebration.store';

const store = useCelebrationStore();

// Generate confetti particles when celebration starts
interface Particle {
  id: number;
  color: string;
  x: number;
  y: number;
  rotation: number;
  rounded: boolean;
  delay: number;
}

const particles = ref<Particle[]>([]);

watch(
  () => store.isVisible,
  (visible) => {
    if (visible) {
      const count = 45;
      const generated: Particle[] = [];
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const distance = 120 + Math.random() * 200;
        generated.push({
          id: i,
          color: i % 2 === 0 ? '#CC0000' : '#00A550',
          x: Math.cos(angle) * distance,
          y: Math.sin(angle) * distance * 0.6 - Math.random() * 180,
          rotation: Math.random() * 720 - 360,
          rounded: Math.random() > 0.5,
          delay: Math.random() * 0.15,
        });
      }
      particles.value = generated;
    } else {
      particles.value = [];
    }
  },
);

const particleStyles = computed(() =>
  particles.value.map((p) => ({
    '--tx': `${p.x}px`,
    '--ty': `${p.y + 400}px`,
    '--rot': `${p.rotation}deg`,
    backgroundColor: p.color,
    borderRadius: p.rounded ? '50%' : '2px',
    animationDelay: `${0.3 + p.delay}s`,
  })),
);
</script>

<template>
  <Transition name="celebration">
    <div
      v-if="store.isVisible"
      class="celebration-overlay"
      aria-live="polite"
    >
      <!-- Dark backdrop -->
      <div class="celebration-backdrop" />

      <!-- Content container -->
      <div class="celebration-content">
        <!-- Devils logo -->
        <img
          src="/devils-logo.png"
          alt="Cardiff Devils"
          class="celebration-logo"
          width="120"
          height="120"
        />

        <!-- Confetti particles -->
        <div class="confetti-container">
          <div
            v-for="(p, i) in particles"
            :key="p.id"
            class="confetti-particle"
            :style="particleStyles[i]"
          />
        </div>

        <!-- Text -->
        <div class="celebration-text">
          <p class="celebration-main-text">{{ store.mainText }}</p>
          <p class="celebration-sub-text">{{ store.subText }}</p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* ── Overlay ── */
.celebration-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  animation: celebration-fadeout 0.5s ease-in 1.5s forwards;
}

.celebration-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  animation: backdrop-in 0.3s ease-out forwards;
}

@keyframes backdrop-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes celebration-fadeout {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* ── Content ── */
.celebration-content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
}

/* ── Logo ── */
.celebration-logo {
  width: 120px;
  height: 120px;
  object-fit: contain;
  animation: logo-celebrate 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both;
}

@keyframes logo-celebrate {
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 0;
  }
  40% {
    transform: scale(1.4) rotate(360deg);
    opacity: 1;
  }
  60% {
    transform: scale(1.1) rotate(360deg);
  }
  100% {
    transform: scale(1) rotate(360deg);
  }
}

/* ── Confetti ── */
.confetti-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 0;
  height: 0;
  pointer-events: none;
}

.confetti-particle {
  position: absolute;
  width: 8px;
  height: 8px;
  top: 0;
  left: 0;
  opacity: 0;
  animation: confetti-burst 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

@keyframes confetti-burst {
  0% {
    transform: translate(0, 0) rotate(0deg) scale(1);
    opacity: 1;
  }
  30% {
    opacity: 1;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) rotate(var(--rot)) scale(0.4);
    opacity: 0;
  }
}

/* ── Text ── */
.celebration-text {
  margin-top: 20px;
  text-align: center;
  animation: text-appear 0.4s ease-out 0.4s both;
}

@keyframes text-appear {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.celebration-main-text {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 48px;
  color: #ffffff;
  letter-spacing: 0.08em;
  line-height: 1;
  margin: 0;
  text-shadow: 0 2px 20px rgba(204, 0, 0, 0.5);
}

.celebration-sub-text {
  font-family: 'DM Mono', monospace;
  font-size: 32px;
  color: #F0C040;
  letter-spacing: 0.05em;
  line-height: 1;
  margin: 8px 0 0;
  text-shadow: 0 2px 12px rgba(240, 192, 64, 0.4);
}

/* ── Vue transition hooks ── */
.celebration-enter-active {
  transition: opacity 0.1s ease;
}
.celebration-leave-active {
  transition: opacity 0.01s ease;
}
.celebration-enter-from,
.celebration-leave-to {
  opacity: 0;
}

/* ── Mobile responsive ── */
@media (max-width: 400px) {
  .celebration-main-text {
    font-size: 36px;
  }
  .celebration-sub-text {
    font-size: 24px;
  }
  .celebration-logo {
    width: 90px;
    height: 90px;
  }
}
</style>
